import { useAppSelector } from '@/app/hooks';
import { useIsProjectDemo } from '@/features/projectDemo';
import type { MessageId } from '@/assets/lang/en-US';
import { ViewModeOptions } from '@/features/types';
import { useMemo } from 'react';
import { Download, Minus, FilePlus, Plus } from 'react-feather';

interface BulkAction {
  labelId: MessageId;
  action?: () => void;
  disabled?: boolean;
  icon?: React.ComponentType<{ size?: string | number }>;
}

export interface BulkActionCallbacks {
  onSpanExport?: () => void;
  onCircuitExport?: () => void;
  onAddToList?: () => void;
  onAddActivity?: () => void;
  onRemoveFromList?: () => void;
}

export function useBulkActionLogic(callbacks: BulkActionCallbacks = {}) {
  const {
    currentViewMode,
    spanNetworkGroup,
    selectedNetworkGroup,
    activeGroupName,
  } = useAppSelector((s) => s.analysisMap);
  const isProjectDemo = useIsProjectDemo();

  const { isSelected: isFilterSelected } = useAppSelector((s) => s.filterQuery);

  const actionsDefinitions = useMemo(() => {
    const exportSpansAction: BulkAction = {
      labelId: 'action.exportGeneral',
      action: callbacks.onSpanExport,
      icon: Download,
    };
    const exportCircuitsAction: BulkAction = {
      labelId: 'action.exportGeneral',
      action: callbacks.onCircuitExport,
      icon: Download,
    };
    const addToProjectAction: BulkAction = {
      labelId: 'customList.addToProject',
      action: callbacks.onAddToList,
      icon: Plus,
      disabled: !isFilterSelected && !activeGroupName,
    };
    const addActivityAction: BulkAction = {
      labelId: 'worklog.addActivity',
      action: callbacks.onAddActivity,
      icon: FilePlus,
      disabled:
        !isFilterSelected &&
        !activeGroupName &&
        currentViewMode.mode !== ViewModeOptions.CUSTOM_LIST_DETAIL,
    };
    const removeFromProjectAction: BulkAction = {
      labelId: 'customList.removeFromProject',
      action: callbacks.onRemoveFromList,
      icon: Minus,
      disabled:
        !isFilterSelected ||
        currentViewMode.mode !== ViewModeOptions.CUSTOM_LIST_DETAIL,
    };

    return {
      exportSpansAction,
      addToProjectAction,
      addActivityAction,
      exportCircuitsAction,
      removeFromProjectAction,
    };
  }, [
    activeGroupName,
    callbacks.onAddActivity,
    callbacks.onAddToList,
    callbacks.onCircuitExport,
    callbacks.onSpanExport,
    callbacks.onRemoveFromList,
    currentViewMode.mode,
    isFilterSelected,
  ]);

  const actions: BulkAction[] = useMemo(() => {
    const {
      exportCircuitsAction,
      exportSpansAction,
      addToProjectAction,
      addActivityAction,
      removeFromProjectAction,
    } = actionsDefinitions;
    switch (currentViewMode.mode) {
      case ViewModeOptions.LEGACY:
        if (
          spanNetworkGroup?.id === selectedNetworkGroup?.id ||
          activeGroupName
        ) {
          return isProjectDemo
            ? [exportSpansAction, addActivityAction]
            : [exportSpansAction, addToProjectAction, addActivityAction];
        } else {
          // The only time we should be in this state is if we're handling the
          // special case of exporting circuits inside of `WORKLOAD_ANALYSIS`
          // on Staging:
          // https://20treeai.slack.com/archives/C06EGKVQC3G/p1758574104526509?thread_ts=1758572487.017969&cid=C06EGKVQC3G
          return [exportCircuitsAction];
        }
      case ViewModeOptions.CUSTOM_LIST_DETAIL:
        return [exportSpansAction, addActivityAction, removeFromProjectAction];
      case ViewModeOptions.CUSTOM_LIST_SPAN:
        return [exportSpansAction, addActivityAction];

      default:
        return [];
    }
  }, [
    actionsDefinitions,
    isProjectDemo,
    currentViewMode.mode,
    spanNetworkGroup?.id,
    selectedNetworkGroup?.id,
    activeGroupName,
  ]);

  return actions;
}
